import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { StagePanel } from './panels/StagePanel';
import { LiveWorkStage } from './LiveWorkStage';
import { DiffBody, MailBody, SlackBody } from './panels/WorkPanels';
import { KostenBody, LisaBody, RenderBody } from './panels/InfoPanels';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import { useDb } from '../../hooks/useDb';
import type { DeskPanel, DeskStage } from '../../hooks/useDeskStage';
const EASE = [0.23, 1, 0.32, 1] as const;
function PanelContent({
  panel,
  stage
}: {
  panel: DeskPanel;
  stage: DeskStage;
}) {
  const pr = useDb(t => t.pullRequests[0]);
  const where = useDb(t => t.renders[0].where);
  const common = {
    pinned: panel.pinned,
    onPin: () => stage.togglePin(panel.uid),
    onDismiss: () => stage.dismiss(panel.uid),
    onOpenBeheer: () => stage.showToast('Beheer komt in een volgende stap.', 'meldt')
  };
  switch (panel.kind) {
    case 'diff':
      return <StagePanel {...common} agent="code" kicker={`PR #${pr.number} · ${pr.repo}`} title={pr.title} level="zelf" where="lokaal" traceId="diff-142" data-id="element-605" data-mp="3iskencpyoh6j">
          <DiffBody onOpenGithub={() => stage.showToast('GitHub opent in je browser.', 'meldt')} data-id="element-606" data-mp="3npawa4hezku2" />
        </StagePanel>;
    case 'slack':
      return <StagePanel {...common} agent="slack" kicker="vraag aan jou" title="Klant Bakker in #project-x" level="naOk" where="lokaal" traceId="slack-bakker" data-id="element-607" data-mp="eb8z0g5y6im">
          <SlackBody onPosted={() => stage.approved('slack', 'Antwoord geplaatst in #project-x')} data-id="element-608" data-mp="2zvj8vpol2d1j" />
        </StagePanel>;
    case 'mail':
      return <StagePanel {...common} agent="mail" kicker="concept" title="Antwoord aan klant Bakker" level="naOk" where="cloud" traceId="mail-bakker" data-id="element-609" data-mp="38t37sryz3x8z">
          <MailBody onSent={() => stage.approved('mail', 'Mail aan klant Bakker verstuurd')} data-id="element-610" data-mp="o3no0gy698su" />
        </StagePanel>;
    case 'render':
      return <StagePanel {...common} agent="studio" kicker="render klaar" title="Beeld voor de nieuwe landingspagina" level="zelf" where={where} traceId="render-landing" data-id="element-611" data-mp="t15p5l5y6jqa">
          <RenderBody onUse={t => stage.showToast(`Toegevoegd aan ${t}`, 'klaar')} data-id="element-612" data-mp="2l51lnsma6w36" />
        </StagePanel>;
    case 'lisa':
      return <StagePanel {...common} agent="initiatief" kicker="14:00 · nieuwe klant" title="Voorbereiding Lisa" where="lokaal" traceId="meeting-lisa" data-id="element-613" data-mp="2z1ylgjkdnnw7">
          <LisaBody data-id="element-614" data-mp="26elzaai4pi3f" />
        </StagePanel>;
    case 'kosten':
      return <StagePanel {...common} agent="initiatief" kicker="cloudkosten" title="Kosten vandaag per agent" level="zelf" where="lokaal" traceId="cloud-budget" data-id="element-615" data-mp="21gkgzrsj1mt0">
          <KostenBody data-id="element-616" data-mp="2g6hyjovybe98" />
        </StagePanel>;
    default:
      return null;
  }
}

/** Center stage: what Anna or the owner is working on right now. Panels arrive from Anna (left) and return to her. */
export function DeskStageArea({
  stage
}: {
  stage: DeskStage;
}) {
  const reduce = useMotionPreference();
  return <section aria-label="Podium" className="no-scrollbar relative min-h-0 overflow-y-auto pb-6" data-id="element-617" data-mp="3ud4vd7qvus72">
      <AnimatePresence initial={false} mode="popLayout" data-id="element-618" data-mp="3ubbphuo3gc04">
        {stage.panels.length === 0 && <motion.div key="live" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-619" data-mp="3lcggkato0nt4">
            <LiveWorkStage onOpen={stage.summon} data-id="element-620" data-mp="3pda1qwfq3oib" />
          </motion.div>}
      </AnimatePresence>
      <div className="flex flex-col gap-5" data-id="element-621" data-mp="gzbc0cjcgzd9">
        <AnimatePresence mode="popLayout" data-id="element-622" data-mp="gwfbcgg43wl4">
          {stage.panels.map(p => <motion.div key={p.uid} layout={!reduce} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          x: -220,
          scale: 0.6
        }} animate={{
          opacity: 1,
          x: 0,
          scale: 1
        }} exit={reduce ? {
          opacity: 0,
          transition: {
            duration: 0.2
          }
        } : {
          opacity: 0,
          x: -260,
          scale: 0.3,
          transition: {
            duration: 0.28,
            ease: EASE
          }
        }} transition={reduce ? {
          duration: 0.2
        } : {
          type: 'spring',
          stiffness: 340,
          damping: 30
        }} style={{
          transformOrigin: 'left center'
        }} data-id="element-623" data-mp="lwjt09sraxcx">
              <PanelContent panel={p} stage={stage} data-id="element-624" data-mp="385k9o3zfkqxb" />
            </motion.div>)}
        </AnimatePresence>
      </div>
    </section>;
}